import React, { Component } from "react";
import { connect } from "react-redux";
import isEmpty from "lodash/isEmpty";
import { push } from "react-router-redux";
import { bool, func, arrayOf, number, shape } from "prop-types";
import classNames from "classnames";

import Head from "pages/_components/Head";
import Notification from "pages/_components/Notification";
import MainContainer from "pages/_components/MainContainer";
import Container from "pages/_components/Container";
import EquivalentTotalBalance from "pages/accounts/_components/EquivalentTotalBalance";
import AccountsList from "pages/accounts/List";
import GeneralMsg from "pages/_components/GeneralMsg";

import * as i18n from "util/i18n";
import * as config from "util/config";

import { selectors as sessionSelectors } from "reducers/session";
import { selectors as settingsSelectors } from "reducers/settings";
import { actions as accountsActions, selectors as accountsSelectors } from "reducers/accounts";
import { actions as statusActions } from "reducers/status";
import { isMobileNative } from "util/device";
import { Box, Button } from "@ui-kit/components/index.es";
import { getRutPathAccount } from "util/accounts";

class Accounts extends Component {
    static propTypes = {
        isRequestAvailable: bool,
        accounts: arrayOf({ length: number.isRequired }),
        dispatch: func.isRequired,
        isMobile: bool,
        fetching: bool,
        ARSTotalBalance: number,
        USDTotalBalance: number,
        loggedUser: func.isRequired,
        fetchingDownload: bool,
        isDesktop: bool,
        hideAmount: bool.isRequired,
        activeEnvironment: shape({}).isRequired,
        location: shape({}),
    };

    static defaultProps = {
        isRequestAvailable: false,
        isMobile: false,
        fetching: false,
        accounts: [],
        ARSTotalBalance: null,
        USDTotalBalance: null,
        fetchingDownload: false,
        isDesktop: true,
        location: {},
    };

    state = {
        showPartialAmounts: false,
    };

    componentDidMount() {
        const { dispatch, loggedUser, location } = this.props;
        const rut = getRutPathAccount();
        if (rut === "/v2/accounts" || rut === "/v3/accounts") {
            dispatch(push(rut));
        } else {
            dispatch(accountsActions.listAccounts(loggedUser.userId));
            dispatch(statusActions.saveLastHref(location));
            dispatch(accountsActions.accountsLastHref(location.pathname));
            window.addEventListener("focus", this.handleFocus);
        }
    }

    componentWillUnmount() {
        window.removeEventListener("focus", this.handleFocus);
    }

    handleFocus = () => {
        if (isMobileNative) {
            window.location.reload();
        }
    };

    handleBack = () => {
        const { dispatch } = this.props;
        dispatch(push("/desktop"));
    };

    handleExport = (format) => {
        const { dispatch } = this.props;
        dispatch(accountsActions.downloadAccountsList(format));
    };

    renderHeader = () => {
        const {
            accounts,
            activeEnvironment,
            ARSTotalBalance,
            dispatch,
            fetching,
            fetchingDownload,
            isMobile,
            USDTotalBalance,
            hideAmount,
        } = this.props;
        const { showPartialAmounts } = this.state;
        const showNewAccountButton = activeEnvironment && activeEnvironment?.permissions.accountOpening;
        const transferAccess =
            activeEnvironment.permissions.transferInternal || activeEnvironment.permissions.transferThirdParties;
        const enableHideAmounts = config.getBoolean("frontend.hide.accounts.balance.funcionalities", false);

        if (isMobile) {
            return (
                <>
                    <div className="admin-detail-head px-0">
                        {showNewAccountButton ? (
                            <Head
                                onBack={this.handleBack}
                                title="accounts.page.header"
                                titleClassName="header-mobile-title-background-blue"
                                exportList={!fetching}
                                handleCreateMessage="accounts.page.newAccount"
                                handleClick={this.handleExport}
                                handleClickMessage="accounts.page.balance.download"
                                isFetchingExport={false}
                                hasInlineButtons={!fetching}
                                csvDownload={!fetching}
                                headerClassName="blue-main-header-mobile"
                                hideNavbarInMobile
                                downloadImageWhite={!fetching}
                                rightContent={() => (
                                    <Box flex>
                                        {enableHideAmounts && hideAmount && !fetching && (
                                            <Button
                                                aria-label="Mostrar saldos durante 3 segundos"
                                                icon={!showPartialAmounts ? "EyeOff" : "Eye"}
                                                iconVariant="outline"
                                                onClick={() => {
                                                    this.setState({ showPartialAmounts: true });
                                                    setTimeout(() => {
                                                        this.setState({ showPartialAmounts: false });
                                                    }, 3000);
                                                }}
                                                style={{ zIndex: 1000 }}
                                                className="px-2"
                                            />
                                        )}
                                        {!fetching && (
                                            <Button
                                                aria-label="Abrir cuenta"
                                                icon="Add"
                                                iconVariant="outline"
                                                onClick={() => {
                                                    dispatch(push(`/accountOpening`));
                                                }}
                                                style={{ zIndex: 1000 }}
                                                className="px-2"
                                            />
                                        )}
                                    </Box>
                                )}
                            />
                        ) : (
                            <Head
                                onBack={this.handleBack}
                                title="accounts.page.header"
                                titleClassName="header-mobile-title-background-blue"
                                exportList
                                handleClick={this.handleExport}
                                handleClickMessage="accounts.page.balance.download"
                                isFetchingExport={false}
                                hasInlineButtons
                                csvDownload
                                headerClassName="blue-main-header-mobile"
                                hideNavbarInMobile
                                downloadImageWhite
                                rightContent={() => (
                                    <Box flex>
                                        {enableHideAmounts && hideAmount && !fetching && (
                                            <Button
                                                aria-label="Mostrar saldos durante 3 segundos"
                                                icon={!showPartialAmounts ? "EyeOff" : "Eye"}
                                                iconVariant="outline"
                                                onClick={() => {
                                                    this.setState({ showPartialAmounts: true });
                                                    setTimeout(() => {
                                                        this.setState({ showPartialAmounts: false });
                                                    }, 3000);
                                                }}
                                                className="z-index-1 px-2"
                                            />
                                        )}
                                    </Box>
                                )}
                            />
                        )}
                    </div>
                    {!isEmpty(accounts) && (ARSTotalBalance || USDTotalBalance) ? (
                        <EquivalentTotalBalance {...this.props} showPartialAmounts={showPartialAmounts} />
                    ) : (
                        <div className="mb-4" />
                    )}
                </>
            );
        }

        return (
            <>
                <div className="admin-detail-head px-0">
                    <Head onBack={this.handleBack} />
                    {showNewAccountButton && (
                        <Head
                            title="accounts.page.header"
                            exportList
                            transfer={transferAccess}
                            handleCreateClick={() => {
                                dispatch(push(`/accountOpening`));
                            }}
                            handleCreateMessage="accounts.page.newAccount"
                            handleClick={this.handleExport}
                            handleClickMessage="accounts.page.balance.download"
                            headerClassName="accounts-header-desktop"
                            isFetchingExport={fetchingDownload}
                            hasInlineButtons
                            csvDownload
                            xlsDownload
                            imageStyle="mr-2"
                        />
                    )}
                    {!showNewAccountButton && (
                        <Head
                            title="accounts.page.header"
                            exportList
                            transfer={transferAccess}
                            handleClick={this.handleExport}
                            handleClickMessage="accounts.page.balance.download"
                            isFetchingExport={fetchingDownload}
                            hasInlineButtons
                            csvDownload
                            xlsDownload
                            headerClassName="accounts-header-desktop"
                            titleClassName="my-0"
                            imageStyle="mr-2"
                        />
                    )}
                    {!isEmpty(accounts) ? <EquivalentTotalBalance {...this.props} /> : ""}
                </div>
            </>
        );
    };

    render() {
        const { accounts, fetching, isDesktop, location } = this.props;
        const { showPartialAmounts } = this.state;

        return (
            <>
                <Notification scopeToShow="accounts" />
                {(!fetching || !isDesktop) && this.renderHeader()}
                <MainContainer
                    showLoader={fetching}
                    loadingClassName="screen-loader"
                    viewContentClassName="overflow-visible"
                    className="main-container without-auto-margin">
                    <div className={classNames({ "mx-3": !isDesktop }, { "pb-7": !isDesktop })}>
                        <Container className={isDesktop ? "align-items-center " : ""}>
                            <div className="above-the-fold">
                                {accounts.length ? (
                                    <AccountsList
                                        extendedData
                                        accounts={accounts}
                                        isDesktop={isDesktop}
                                        showPartialAmounts={showPartialAmounts}
                                        servicePaymentsOriginPathname={location?.pathname}
                                    />
                                ) : (
                                    <GeneralMsg
                                        imagePath="images/coloredIcons/find-in-page.svg"
                                        description={i18n.get("accounts.list.empty")}
                                        isDesktop={isDesktop}
                                    />
                                )}
                            </div>
                        </Container>
                    </div>
                </MainContainer>
            </>
        );
    }
}

const mapStateToProps = (state) => ({
    accounts: accountsSelectors.getAccounts(state),
    activeEnvironment: sessionSelectors.getActiveEnvironment(state),
    loggedUser: sessionSelectors.getUser(state),
    ARSTotalBalance: accountsSelectors.getARSTotalBalance(state),
    USDTotalBalance: accountsSelectors.getUSDTotalBalance(state),
    fetching: accountsSelectors.getFetching(state),
    fetchingDownload: accountsSelectors.getFetchingDownload(state),
    isRequestAvailable: sessionSelectors
        .getActiveEnvironmentForms(state, "accounts")
        .find(({ idForm }) => idForm === "accountOpening"),
    hideAmount: settingsSelectors.getUserData(state)?.hideBalance,
});

export default connect(mapStateToProps)(Accounts);
